import { Fetcher } from '@this/src/util';
import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import moment from 'moment';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import type { ReceiptTripDownloadArgs } from '@this/domain/accounting/receipts/receipt_trip_download';
import ReceiptTripDownload from '@this/domain/accounting/receipts/receipt_trip_download';

import Pagination from '@this/shared/pagination/pagination';
import ReceiptTripList from '@this/components/organization/receipts/receipt_trip_list';
import { Box } from '@material-ui/core';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Input } from '@this/components/shared/ui/inputs/input';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';
import type { Moment } from 'moment';
import { Select } from '@this/components/shared/ui/inputs/select';
import { OrganizationTitle, OrganizationBody } from '@this/components/organization/organization.style';
import { Alert } from '@this/shared/ui/feedbacks/alert';

interface Response {
  receipt_trip_downloads: ReceiptTripDownloadArgs[];
  total_pages: number;
  current_page: number;
}

const Receipts = observer(() => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [csvDownloadError, setCsvDownloadError] = useState<string | null>(null);
  const [receiptTripDownloads, setReceiptTripDownloads] = useState<ReceiptTripDownload[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState(1);
  const history = useHistory();

  const [tripId, setTripId] = useState<string>('');
  const [receiptRevisionStatus, setReceiptRevisionStatus] = useState<string>('');
  const [from, setFrom] = useState<Moment | undefined>(moment().subtract(1, 'months'));
  const [to, setTo] = useState<Moment | undefined>(moment());
  const [csvEncoding, setCsvEncoding] = useState<string>('utf8');

  const fetchData = useCallback(
    async (page: number) => {
      setIsLoading(true);
      setFetchError(null);

      const params = {
        from: from ? from.format('YYYY-MM-DD') : null,
        to: to ? to.format('YYYY-MM-DD') : null,
        trip_id: toHalfWidth(tripId),
        receipt_revision_status: receiptRevisionStatus || null,
        page
      };

      try {
        const res = await Fetcher.get<Response>('/organization/receipts.json', params);
        setReceiptTripDownloads(res.receipt_trip_downloads.map(data => new ReceiptTripDownload(data)));
        setCurrentPage(res.current_page);
        setTotalPage(res.total_pages);
      } catch (e) {
        setFetchError('エラーが発生しました。');
      }
      setIsLoading(false);
    },
    [tripId, receiptRevisionStatus, from, to]
  );

  useEffect(() => {
    history.push(`/organization/receipts`);
    fetchData(currentPage);
  }, [currentPage]);

  const toHalfWidth = (str: string): string => {
    return str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
  };

  const handleCsvDownload = async () => {
    setCsvDownloadError(null);
    try {
      const params = {
        from: from ? from.format('YYYY-MM-DD') : null,
        to: to ? to.format('YYYY-MM-DD') : null,
        trip_id: toHalfWidth(tripId),
        receipt_revision_status: receiptRevisionStatus || null,
        encoding: csvEncoding
      };

      const queryString = Object.entries(params)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&');

      window.location.href = `/organization/receipts/export_csv.csv?${queryString}`;
    } catch (e) {
      console.error('CSVダウンロードに失敗しました', e);
      setCsvDownloadError('CSVダウンロードに失敗しました。');
    }
  };

  return (
    <>
      <OrganizationTitle>領収書一覧</OrganizationTitle>
      <OrganizationBody>
        <Box mb={2} display="flex" alignItems="flex-end" gridGap="10px">
          <Box>
            <label>最終ダウンロード日</label>
            <Box display="flex" alignItems="center" gridGap="4px">
              <Datepicker
                value={from?.toDate()}
                onChange={value => setFrom(value ? moment(value) : undefined)}
                placeholder="開始日"
              />
              〜
              <Datepicker
                value={to?.toDate()}
                onChange={value => setTo(value ? moment(value) : undefined)}
                placeholder="終了日"
              />
            </Box>
          </Box>
          <Box>
            <label>領収書ステータス</label>
            <Select
              value={receiptRevisionStatus}
              onChange={e => setReceiptRevisionStatus(e.target.value)}
              style={{ width: '180px' }}
            >
              <option value="">全て</option>
              <option value="0">発行済</option>
              <option value="1">破棄訂正中</option>
              <option value="2">破棄訂正済</option>
            </Select>
          </Box>
          <Box>
            <label>旅程番号</label>
            <Input type="text" placeholder="旅程番号" value={tripId} onChange={e => setTripId(e.target.value)} />
          </Box>
          <Button color="primary" onClick={() => fetchData(1)}>
            検索
          </Button>
        </Box>
        <Box mb={2} display="flex" alignItems="flex-end" gridGap="10px">
          <Box>
            <label>CSVエクスポート</label>
            <Select value={csvEncoding} onChange={e => setCsvEncoding(e.target.value)} style={{ width: '120px' }}>
              <option value="utf8">UTF-8</option>
              <option value="sjis">Shift-JIS</option>
            </Select>
          </Box>
          <Box>
            <Button color="primary" onClick={handleCsvDownload}>
              CSVダウンロード
            </Button>
          </Box>
        </Box>

        {csvDownloadError && (
          <Box mt={2}>
            <Alert variant="danger" onClose={() => setCsvDownloadError(null)}>
              {csvDownloadError}
            </Alert>
          </Box>
        )}

        <Box mt={4}>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <Box mt={2}>
                <ReceiptTripList receiptTripDownloads={receiptTripDownloads} />
              </Box>
              <Box mt={2}>
                <Pagination
                  currentPage={currentPage}
                  totalPage={totalPage}
                  handleSearch={page => setCurrentPage(page)}
                />
              </Box>
            </>
          )}
        </Box>

        {fetchError && <Box color="red">{fetchError}</Box>}
      </OrganizationBody>
    </>
  );
});

export default Receipts;
